<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaAuth } from 'dashboard/composables/useArdhisasaAuth';
import { usePiiRedaction } from 'dashboard/composables/usePiiRedaction';
import ArdhisasaAuthModal from '../tickets/components/ArdhisasaAuthModal.vue';
import * as api from 'dashboard/api/ardhisasaUsers';
import {
  getNrbDetails,
  getForeignersDetails,
} from 'dashboard/api/ardhisasaUsers';

const { t } = useI18n();

const SEARCH_TYPES = [
  { key: 'company', label: t('ARDHISASA.USERS.SEARCH.TYPE_COMPANY') },
  { key: 'staff', label: t('ARDHISASA.USERS.SEARCH.TYPE_STAFF') },
  { key: 'id_type', label: t('ARDHISASA.USERS.SEARCH.TYPE_ID_TYPE') },
  { key: 'nrb', label: t('ARDHISASA.USERS.SEARCH.TYPE_NRB') },
];

const NRB_TYPES = [
  { value: 'kenyans', label: t('ARDHISASA.USERS.NRB.KENYANS') },
  { value: 'foreigners', label: t('ARDHISASA.USERS.NRB.FOREIGNERS') },
];

const ID_TYPES = [
  { value: 'INDIVIDUAL', label: t('ARDHISASA.USERS.ID_TYPES.INDIVIDUAL') },
  { value: 'PASSPORT', label: t('ARDHISASA.USERS.ID_TYPES.PASSPORT') },
  { value: 'ALIEN_ID', label: t('ARDHISASA.USERS.ID_TYPES.ALIEN_ID') },
  { value: 'ARDHISASA', label: t('ARDHISASA.USERS.ID_TYPES.ARDHISASA') },
];

const {
  showAuth,
  handleAuthenticated,
  dismissAuth,
  store: authStore,
} = useArdhisasaAuth();

// ── Redaction ─────────────────────────────────────────────────────────────────
const {
  isAdmin,
  redactPersonName,
  redactEmail,
  redactPhone,
  redactId,
  redactKra,
  redactEmployeeNum,
} = usePiiRedaction();

function toSentenceCase(str) {
  if (!str) return '—';
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

function formatShares(shares) {
  if (!shares || !shares.length) return '—';
  return shares
    .map(
      s =>
        `${Number(s.number_of_shares).toLocaleString()} ${toSentenceCase(s.name)}`
    )
    .join(', ');
}

function displayName(profile) {
  if (!profile) return '—';
  const full =
    [profile.firstname, profile.middlename, profile.lastname]
      .filter(Boolean)
      .join(' ') ||
    profile.name ||
    '';
  if (!full) return '—';
  return redactPersonName(full);
}

// ── Search state ──────────────────────────────────────────────────────────────
const searchType = ref('company');
const loading = ref(false);
const error = ref('');
const results = ref([]);
const resultsMeta = ref({ count: 0, next: null, previous: null });
const staffPage = ref(1);

const companyReg = ref('');
const staffEmployee = ref('');
const staffName = ref('');
const idTypeValue = ref('');
const idTypeSelected = ref('INDIVIDUAL');
const nrbIdnum = ref('');
const nrbType = ref('kenyans');
const nrbResult = ref(null);

const companyDirectorsInfo = ref(null);
const directorsLoading = ref(false);

const hasResults = computed(() => results.value.length > 0);

const selectedIDTypeLabel = computed(
  () => ID_TYPES.find(opt => opt.value === idTypeSelected.value)?.label ?? ''
);

function selectType(key) {
  searchType.value = key;
  results.value = [];
  nrbResult.value = null;
  companyDirectorsInfo.value = null;
  error.value = '';
  staffPage.value = 1;
}

async function fetchCompanyDirectors(registrationNumber) {
  directorsLoading.value = true;
  try {
    const { token, jwt } = authStore;
    const { data } = await api.getCompanyDirectors(
      token,
      jwt,
      registrationNumber
    );
    companyDirectorsInfo.value = Array.isArray(data) ? data[0] : data;
  } catch (e) {
    companyDirectorsInfo.value = null;
  } finally {
    directorsLoading.value = false;
  }
}

function statusStyle(status) {
  return status === 'ACTIVE'
    ? { background: '#16a34a', color: '#fff' }
    : { background: '#64748b', color: '#fff' };
}

function boolStyle(val) {
  return val
    ? { background: '#16a34a', color: '#fff' }
    : { background: '#64748b', color: '#fff' };
}

function getProfile(user) {
  return user.profile || user;
}

async function search() {
  error.value = '';
  results.value = [];
  nrbResult.value = null;
  companyDirectorsInfo.value = null;
  loading.value = true;
  try {
    const { token, jwt } = authStore;
    let data;

    if (searchType.value === 'company') {
      if (!companyReg.value.trim()) {
        error.value = t('ARDHISASA.USERS.ERRORS.REQUIRED');
        return;
      }
      const registrationNumber = companyReg.value.trim();
      ({ data } = await api.filterByCompany(token, jwt, registrationNumber));
      results.value = [data];
      fetchCompanyDirectors(registrationNumber);
    } else if (searchType.value === 'staff') {
      if (!staffEmployee.value.trim() && !staffName.value.trim()) {
        error.value = t('ARDHISASA.USERS.ERRORS.STAFF_REQUIRED');
        return;
      }
      ({ data } = await api.filterStaff(token, jwt, {
        employeeNumber: staffEmployee.value.trim() || undefined,
        name: staffName.value.trim() || undefined,
        page: staffPage.value,
      }));
      results.value = data.results || [];
      resultsMeta.value = {
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
      };
    } else if (searchType.value === 'id_type') {
      if (!idTypeValue.value.trim()) {
        error.value = t('ARDHISASA.USERS.ERRORS.REQUIRED');
        return;
      }
      ({ data } = await api.searchByIdType(
        token,
        jwt,
        idTypeValue.value.trim(),
        idTypeSelected.value
      ));
      results.value = [
        {
          profile: data,
          account_status: data.account_status,
          account_number: data.account_number,
        },
      ];
    } else if (searchType.value === 'nrb') {
      if (!nrbIdnum.value.trim()) {
        error.value = t('ARDHISASA.USERS.ERRORS.REQUIRED');
        return;
      }
      const fn =
        nrbType.value === 'foreigners' ? getForeignersDetails : getNrbDetails;
      ({ data } = await fn(token, jwt, nrbIdnum.value.trim()));
      nrbResult.value = data;
    }
  } catch (e) {
    const msg = e.response?.data?.details;
    error.value =
      typeof msg === 'string' ? msg : t('ARDHISASA.USERS.ERRORS.GENERIC');
  } finally {
    loading.value = false;
  }
}

async function prevPage() {
  if (!resultsMeta.value.previous) return;
  staffPage.value -= 1;
  await search();
}

async function nextPage() {
  if (!resultsMeta.value.next) return;
  staffPage.value += 1;
  await search();
}
</script>

<template>
  <div
    class="flex flex-col flex-1 w-full h-full min-h-0 overflow-hidden bg-n-surface-1"
  >
    <ArdhisasaAuthModal
      v-if="showAuth"
      @authenticated="handleAuthenticated"
      @close="dismissAuth"
    />

    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('ARDHISASA.USERS.TITLE') }}
      </h1>
      <span
        v-if="!isAdmin"
        class="text-xs text-n-slate-11 bg-n-alpha-2 px-2 py-1 rounded-full"
      >
        {{ t('ARDHISASA.USERS.REDACTED_NOTICE') }}
      </span>
    </div>

    <!-- Search Panel -->
    <div class="px-6 py-4 border-b border-n-weak flex-shrink-0 space-y-3">
      <!-- Search type tabs — full width -->
      <div class="flex w-full border border-n-weak rounded-lg overflow-hidden">
        <button
          v-for="type in SEARCH_TYPES"
          :key="type.key"
          class="flex-1 px-3 py-2 text-xs font-medium text-center transition-colors border-r border-n-weak last:border-r-0"
          :class="
            searchType === type.key
              ? 'bg-[#71481d] text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2 bg-white'
          "
          @click="selectType(type.key)"
        >
          {{ type.label }}
        </button>
      </div>

      <!-- Dynamic search form — full width -->
      <div class="flex items-end gap-3 w-full">
        <!-- Company -->
        <template v-if="searchType === 'company'">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.SEARCH.COMPANY_LABEL') }}
            </label>
            <input
              v-model="companyReg"
              type="text"
              :placeholder="t('ARDHISASA.USERS.SEARCH.COMPANY_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
              @keyup.enter="search"
            />
          </div>
        </template>

        <!-- Staff -->
        <template v-else-if="searchType === 'staff'">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.SEARCH.EMPLOYEE_LABEL') }}
            </label>
            <input
              v-model="staffEmployee"
              type="text"
              :placeholder="t('ARDHISASA.USERS.SEARCH.EMPLOYEE_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
              @keyup.enter="search"
            />
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.SEARCH.NAME_LABEL') }}
            </label>
            <input
              v-model="staffName"
              type="text"
              :placeholder="t('ARDHISASA.USERS.SEARCH.NAME_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
              @keyup.enter="search"
            />
          </div>
        </template>

        <!-- ID Type -->
        <template v-else-if="searchType === 'id_type'">
          <div class="flex flex-col gap-1 w-64 flex-shrink-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.SEARCH.ID_TYPE_LABEL') }}
            </label>
            <select
              v-model="idTypeSelected"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
            >
              <option
                v-for="opt in ID_TYPES"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.SEARCH.ID_VALUE_LABEL') }}
            </label>
            <input
              v-model="idTypeValue"
              type="text"
              :placeholder="`Enter ${selectedIDTypeLabel}`"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
              @keyup.enter="search"
            />
          </div>
        </template>

        <!-- NRB -->
        <template v-else-if="searchType === 'nrb'">
          <div class="flex flex-col gap-1 w-48 flex-shrink-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.NRB.TYPE_LABEL') }}
            </label>
            <select
              v-model="nrbType"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
            >
              <option
                v-for="opt in NRB_TYPES"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('ARDHISASA.USERS.NRB.ID_LABEL') }}
            </label>
            <input
              v-model="nrbIdnum"
              type="text"
              :placeholder="t('ARDHISASA.USERS.NRB.ID_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white text-n-slate-12 focus:outline-none focus:ring-2"
              @keyup.enter="search"
            />
          </div>
        </template>

        <!-- Search button -->
        <button
          class="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#71481d] text-white text-sm font-medium hover:bg-[#5a3915] disabled:opacity-60"
          :disabled="loading"
          @click="search"
        >
          <span
            v-if="loading"
            class="i-lucide-loader-circle size-4 animate-spin"
          />
          <span v-else class="i-lucide-search size-4" />
          {{ t('ARDHISASA.USERS.SEARCH.BUTTON') }}
        </button>
      </div>

      <p v-if="error" class="text-xs text-red-600 font-medium">
        {{ error }}
      </p>
    </div>

    <!-- Results -->
    <div class="flex-1 overflow-y-auto px-6 py-4">
      <!-- Empty state -->
      <div
        v-if="!loading && !hasResults && !error"
        class="flex flex-col items-center justify-center h-40 text-sm text-n-slate-11 gap-2"
      >
        <span class="i-lucide-users size-8 opacity-40" />
        <span>{{ t('ARDHISASA.USERS.EMPTY') }}</span>
      </div>

      <!-- Staff result count -->
      <p
        v-if="searchType === 'staff' && resultsMeta.count > 0"
        class="text-xs text-n-slate-11 mb-3"
      >
        {{ resultsMeta.count }} {{ t('ARDHISASA.USERS.RESULTS_COUNT') }}
      </p>

      <!-- User cards grid -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          v-for="(user, idx) in results"
          :key="idx"
          class="bg-white border border-n-weak rounded-xl p-5 space-y-4"
        >
          <!-- Top row: avatar + name + badges -->
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <img
                v-if="user.profile_photo"
                :src="user.profile_photo"
                class="w-14 h-14 rounded-full object-cover border border-n-weak"
                alt=""
              />
              <div
                v-else
                class="w-14 h-14 rounded-full bg-[#71481d] flex items-center justify-center text-white text-lg font-bold"
              >
                {{ displayName(getProfile(user)).charAt(0).toUpperCase() }}
              </div>
            </div>

            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12 truncate">
                {{ displayName(getProfile(user)) }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{
                  user.account_number || getProfile(user).account_number || '—'
                }}
              </p>
              <div class="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  class="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                  :style="
                    statusStyle(
                      user.account_status || getProfile(user).account_status
                    )
                  "
                >
                  {{
                    user.account_status ||
                    getProfile(user).account_status ||
                    '—'
                  }}
                </span>
                <span
                  v-if="getProfile(user).usertype"
                  class="text-xs font-medium px-2.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ getProfile(user).usertype }}
                </span>
              </div>
            </div>
          </div>

          <!-- Details grid -->
          <div
            class="grid grid-cols-2 gap-x-8 gap-y-2 text-xs border-t border-n-weak pt-4"
          >
            <template v-if="getProfile(user).email">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-mail size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.EMAIL')
                }}</span>
              </div>
              <span class="text-n-slate-12 truncate">{{
                redactEmail(getProfile(user).email)
              }}</span>
            </template>

            <template
              v-if="getProfile(user).phonenumber || getProfile(user).phonenum"
            >
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-phone size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.PHONE')
                }}</span>
              </div>
              <span class="text-n-slate-12">
                {{
                  redactPhone(
                    getProfile(user).phonenumber || getProfile(user).phonenum
                  )
                }}
              </span>
            </template>

            <template v-if="getProfile(user).idnum">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-id-card size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.ID_NUMBER')
                }}</span>
              </div>
              <span class="text-n-slate-12">{{
                redactId(getProfile(user).idnum)
              }}</span>
            </template>

            <template v-if="getProfile(user).krapin">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-receipt size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.KRA_PIN')
                }}</span>
              </div>
              <span class="text-n-slate-12">{{
                redactKra(getProfile(user).krapin)
              }}</span>
            </template>

            <template v-if="getProfile(user).nationality">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-globe size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.NATIONALITY')
                }}</span>
              </div>
              <span class="text-n-slate-12">{{
                getProfile(user).nationality
              }}</span>
            </template>

            <template
              v-if="
                getProfile(user).registration_number || getProfile(user).reg_num
              "
            >
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-building-2 size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.REG_NUMBER')
                }}</span>
              </div>
              <span class="text-n-slate-12">
                {{
                  getProfile(user).registration_number ||
                  getProfile(user).reg_num
                }}
              </span>
            </template>

            <template v-if="companyDirectorsInfo?.registration_date">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-calendar size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.REG_DATE')
                }}</span>
              </div>
              <span class="text-n-slate-12">
                {{ companyDirectorsInfo.registration_date }}
              </span>
            </template>

            <template v-if="getProfile(user).employeenum">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-badge size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.EMPLOYEE_NO')
                }}</span>
              </div>
              <span class="text-n-slate-12">{{
                redactEmployeeNum(getProfile(user).employeenum)
              }}</span>
            </template>

            <template v-if="getProfile(user).department_details">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-landmark size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.DEPARTMENT')
                }}</span>
              </div>
              <span class="text-n-slate-12 truncate">
                {{
                  getProfile(user).department_details?.department?.name || '—'
                }}
              </span>
            </template>

            <template v-if="getProfile(user).designation">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-briefcase size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.DESIGNATION')
                }}</span>
              </div>
              <span class="text-n-slate-12">
                {{ getProfile(user).designation?.name || '—' }}
              </span>
            </template>

            <template v-if="getProfile(user).physical_address">
              <div class="flex items-center gap-2 text-n-slate-11">
                <span class="i-lucide-map-pin size-3.5" />
                <span class="font-medium">{{
                  t('ARDHISASA.USERS.FIELDS.ADDRESS')
                }}</span>
              </div>
              <span class="text-n-slate-12 truncate">
                {{ getProfile(user).physical_address }}
              </span>
            </template>

            <!-- Profile Photo -->
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-image size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.FIELDS.PROFILE_PHOTO')
              }}</span>
            </div>
            <span
              class="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full w-fit"
              :style="boolStyle(!!user.profile_photo)"
            >
              {{
                user.profile_photo
                  ? t('ARDHISASA.USERS.FIELDS.YES')
                  : t('ARDHISASA.USERS.FIELDS.NO')
              }}
            </span>

            <!-- Signature -->
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-pen-line size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.FIELDS.SIGNATURE')
              }}</span>
            </div>
            <span
              class="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full w-fit"
              :style="
                boolStyle(
                  !!(
                    getProfile(user).signature || getProfile(user).has_signature
                  )
                )
              "
            >
              {{
                getProfile(user).signature || getProfile(user).has_signature
                  ? t('ARDHISASA.USERS.FIELDS.YES')
                  : t('ARDHISASA.USERS.FIELDS.NO')
              }}
            </span>

            <!-- Security Question -->
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-shield-question size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.FIELDS.SECURITY_QUESTION')
              }}</span>
            </div>
            <span
              class="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full w-fit"
              :style="
                boolStyle(
                  !!(
                    getProfile(user).security_question_status ||
                    getProfile(user).security_question ||
                    getProfile(user).has_security_question
                  )
                )
              "
            >
              {{
                getProfile(user).security_question_status ||
                getProfile(user).security_question ||
                getProfile(user).has_security_question
                  ? t('ARDHISASA.USERS.FIELDS.YES')
                  : t('ARDHISASA.USERS.FIELDS.NO')
              }}
            </span>
          </div>

          <!-- Directors -->
          <div
            v-if="
              searchType === 'company' &&
              (directorsLoading || companyDirectorsInfo?.partners?.length)
            "
            class="border-t border-n-weak pt-4 space-y-3"
          >
            <h4
              class="text-xs font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-users size-3.5" />
              {{ t('ARDHISASA.USERS.FIELDS.DIRECTORS') }}
            </h4>

            <div
              v-if="directorsLoading"
              class="flex items-center gap-2 text-xs text-n-slate-11"
            >
              <span class="i-lucide-loader-circle size-3.5 animate-spin" />
              {{ t('ARDHISASA.USERS.FIELDS.DIRECTORS_LOADING') }}
            </div>

            <div
              v-for="(partner, pIdx) in companyDirectorsInfo?.partners || []"
              :key="pIdx"
              class="grid grid-cols-2 gap-x-8 gap-y-2 text-xs border border-n-weak rounded-lg p-3"
            >
              <div class="col-span-2 text-n-slate-12 font-medium truncate">
                {{ redactPersonName(partner.name) }}
              </div>

              <span class="font-medium text-n-slate-11">{{
                t('ARDHISASA.USERS.FIELDS.DIRECTOR_TYPE')
              }}</span>
              <span class="text-n-slate-12">{{
                toSentenceCase(partner.type)
              }}</span>

              <span class="font-medium text-n-slate-11">{{
                t('ARDHISASA.USERS.FIELDS.ID_TYPE')
              }}</span>
              <span class="text-n-slate-12">{{
                toSentenceCase(partner.id_type)
              }}</span>

              <span class="font-medium text-n-slate-11">{{
                t('ARDHISASA.USERS.FIELDS.ID_NUMBER')
              }}</span>
              <span class="text-n-slate-12">{{
                redactId(partner.id_number)
              }}</span>

              <span class="font-medium text-n-slate-11">{{
                t('ARDHISASA.USERS.FIELDS.GENDER')
              }}</span>
              <span class="text-n-slate-12">{{
                toSentenceCase(partner.gender)
              }}</span>

              <span class="font-medium text-n-slate-11">{{
                t('ARDHISASA.USERS.FIELDS.SHARES')
              }}</span>
              <span class="text-n-slate-12">{{
                formatShares(partner.shares)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- NRB result card -->
      <div
        v-if="nrbResult"
        class="bg-white border border-n-weak rounded-xl p-5 space-y-4"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-12 h-12 rounded-full bg-[#71481d] flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
          >
            {{ (nrbResult.full_names || '?').charAt(0) }}
          </div>
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ redactPersonName(nrbResult.full_names) }}
            </h3>
            <span
              class="text-xs font-semibold px-2.5 py-0.5 rounded-full"
              :style="statusStyle(nrbResult.status)"
            >
              {{ nrbResult.status || '—' }}
            </span>
          </div>
        </div>
        <div
          class="grid grid-cols-2 gap-x-8 gap-y-2 text-xs border-t border-n-weak pt-4"
        >
          <div class="flex items-center gap-2 text-n-slate-11">
            <span class="i-lucide-id-card size-3.5" />
            <span class="font-medium">{{
              t('ARDHISASA.USERS.NRB.FIELD_ID')
            }}</span>
          </div>
          <span class="text-n-slate-12">{{
            redactId(nrbResult.id_number)
          }}</span>

          <div class="flex items-center gap-2 text-n-slate-11">
            <span class="i-lucide-hash size-3.5" />
            <span class="font-medium">{{
              t('ARDHISASA.USERS.NRB.FIELD_SERIAL')
            }}</span>
          </div>
          <span class="text-n-slate-12">{{
            redactId(nrbResult.serial_no)
          }}</span>

          <div class="flex items-center gap-2 text-n-slate-11">
            <span class="i-lucide-user size-3.5" />
            <span class="font-medium">{{
              t('ARDHISASA.USERS.NRB.FIELD_MOTHER')
            }}</span>
          </div>
          <span class="text-n-slate-12">{{
            redactPersonName(nrbResult.mother_full_name)
          }}</span>

          <template v-if="nrbResult.gender">
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-users size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.NRB.FIELD_GENDER')
              }}</span>
            </div>
            <span class="text-n-slate-12">{{ nrbResult.gender }}</span>
          </template>

          <template v-if="nrbResult.id_type">
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-fingerprint size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.NRB.FIELD_ID_TYPE')
              }}</span>
            </div>
            <span class="text-n-slate-12">{{ nrbResult.id_type }}</span>
          </template>

          <template v-if="nrbResult.date_end_validity">
            <div class="flex items-center gap-2 text-n-slate-11">
              <span class="i-lucide-calendar size-3.5" />
              <span class="font-medium">{{
                t('ARDHISASA.USERS.NRB.FIELD_EXPIRY')
              }}</span>
            </div>
            <span class="text-n-slate-12">{{
              nrbResult.date_end_validity
            }}</span>
          </template>
        </div>
      </div>

      <!-- Staff pagination -->
      <div
        v-if="searchType === 'staff' && resultsMeta.count > 0"
        class="flex items-center justify-end gap-2 pt-2"
      >
        <button
          :disabled="!resultsMeta.previous"
          class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
          @click="prevPage"
        >
          {{ t('ARDHISASA.TICKETS.PAGINATION.PREVIOUS') }}
        </button>
        <button
          :disabled="!resultsMeta.next"
          class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
          @click="nextPage"
        >
          {{ t('ARDHISASA.TICKETS.PAGINATION.NEXT') }}
        </button>
      </div>
    </div>
  </div>
</template>
